// ==============================================
// Players Listing Page
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { playersAPI } from '../services/api';
import { User } from '../types';
import { playerPath } from '../utils/slug';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

const Players: React.FC = () => {
  const [players, setPlayers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const getLevelBadgeClass = (slug?: string | null) => {
    switch (slug) {
      case 'elite':         return 'cat-elite';
      case 'profesionisti': return 'cat-elite';
      case 'open':          return 'cat-a';
      case 'avansati':      return 'cat-b';
      case 'hobby':         return 'cat-c';
      case 'amator':        return 'cat-d';
      default:              return 'cat-d';
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (selectedLevel) params.level = selectedLevel;

      const response = await playersAPI.search(params);

      if (response.success && response.data) {
        setPlayers((response.data.players as unknown as User[]) || []);
      } else {
        setError(response.message || 'Failed to load players');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedLevel]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPlayers = players.filter(p =>
    p.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="page-header">
          <h2>Gaseste jucatori</h2>
          <p>Conecteaza-te cu alti jucatori de tenis de masa</p>
        </div>

        <div className="filter-bar">
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Cauta dupa nume…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
                <option value="">Toate nivelurile</option>
                <option value="amator">Amator</option>
                <option value="hobby">Hobby</option>
                <option value="avansati">Avansati</option>
                <option value="open">Open</option>
                <option value="elite">Elite</option>
                <option value="profesionisti">Profesionisti</option>
              </select>
            </div>
            <div className="col-md-3">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setSearchTerm(''); setSelectedLevel(''); }}>
                Sterge
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Se incarca…</span></div>
            <span>Se incarca jucatorii…</span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <>
            <p className="result-count">{filteredPlayers.length} {filteredPlayers.length !== 1 ? 'jucatori gasiti' : 'jucator gasit'}</p>

            {filteredPlayers.length === 0 ? (
              <div className="tt-empty">
                <div className="tt-empty-icon">🏓</div>
                <h5>Niciun jucator gasit</h5>
                <p>Incearca sa ajustezi criteriile de cautare</p>
              </div>
            ) : (
              <div className="row g-4">
                {filteredPlayers.map(player => {
                  const raw = player as any;
                  const initials = `${player.first_name?.[0] ?? ''}${player.last_name?.[0] ?? ''}`.toUpperCase();
                  const avatarSrc = player.avatar_url ? `${SITE_URL}${player.avatar_url}` : null;
                  return (
                    <div key={player.id} className="col-md-6 col-lg-4">
                      <div className="entity-card">
                        <div className="entity-card-body">
                          <div className="d-flex gap-3 align-items-center mb-3">
                            <div
                              className="avatar"
                              style={{
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: avatarSrc ? 'transparent' : undefined,
                              }}
                            >
                              {avatarSrc ? (
                                <img
                                  src={avatarSrc}
                                  alt={`${player.first_name} ${player.last_name}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : initials}
                            </div>
                            <div>
                              <div className="fw-semibold">{player.first_name} {player.last_name}</div>
                              {player.level_name && (
                                <span className={`badge mt-1 ${getLevelBadgeClass(player.level_slug)}`}>
                                  {player.level_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="row g-2 text-center">
                            <div className="col-6">
                              <div className="fw-bold" style={{ color: 'var(--tt-primary)' }}>{Number(raw.total_wins ?? 0)}</div>
                              <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)' }}>Victorii</div>
                            </div>
                            <div className="col-6">
                              <div className="fw-bold">{Number(raw.total_matches ?? 0)}</div>
                              <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)' }}>Meciuri</div>
                            </div>
                          </div>
                        </div>
                        <div className="entity-card-footer">
                          <Link to={playerPath(player)} className="btn btn-primary btn-sm w-100">Vezi profil</Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Players;
